import { useState } from "react";
import { Check } from "lucide-react";
import { palettes } from "@/config/site";
import { trackEvent } from "@/lib/analytics";

function shade(hex, amount) {
  const n = parseInt(hex.slice(1), 16);
  const f = (v) => Math.max(0, Math.min(255, v + amount));
  const r = f((n >> 16) & 255);
  const g = f((n >> 8) & 255);
  const b = f(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function RoomPreview({ color }) {
  return (
    <svg viewBox="0 0 480 300" className="h-auto w-full" role="img" aria-label={`Room preview with walls painted in the selected colour ${color}`} data-testid="room-preview">
      <rect x="0" y="0" width="480" height="300" fill="#F4F1EC" />
      {/* back wall */}
      <rect x="90" y="30" width="300" height="200" fill={color} />
      {/* left wall */}
      <polygon points="0,0 90,30 90,230 0,300" fill={shade(color, -26)} />
      {/* right wall */}
      <polygon points="480,0 390,30 390,230 480,300" fill={shade(color, -16)} />
      {/* floor */}
      <polygon points="0,300 90,230 390,230 480,300" fill="#C9B79C" />
      <polygon points="0,300 90,230 390,230 480,300" fill="#00000008" />
      {/* baseboards */}
      <rect x="90" y="222" width="300" height="9" fill="#FFFFFF" />
      <polygon points="0,289 90,222 90,231 0,298" fill="#F1F5F9" />
      <polygon points="480,289 390,222 390,231 480,298" fill="#F1F5F9" />
      {/* window */}
      <rect x="120" y="60" width="90" height="110" rx="3" fill="#EAF3F8" stroke="#FFFFFF" strokeWidth="8" />
      <line x1="165" y1="64" x2="165" y2="166" stroke="#FFFFFF" strokeWidth="5" />
      {/* picture frame */}
      <rect x="270" y="75" width="70" height="55" rx="3" fill="#FFFFFF" />
      <rect x="276" y="81" width="58" height="43" fill={shade(color, 40)} />
      {/* sofa */}
      <rect x="180" y="180" width="140" height="40" rx="10" fill="#3B4552" />
      <rect x="172" y="168" width="20" height="52" rx="8" fill="#333C47" />
      <rect x="308" y="168" width="20" height="52" rx="8" fill="#333C47" />
      <rect x="196" y="172" width="44" height="24" rx="6" fill="#475364" />
      <rect x="248" y="172" width="44" height="24" rx="6" fill="#475364" />
      {/* plant */}
      <rect x="52" y="205" width="26" height="30" rx="4" fill="#8A6F57" />
      <ellipse cx="65" cy="192" rx="20" ry="18" fill="#4D6B57" />
    </svg>
  );
}

export function PaletteSelector() {
  const [paletteId, setPaletteId] = useState(palettes[0].id);
  const [selected, setSelected] = useState(palettes[0].colors[0]);
  const palette = palettes.find((p) => p.id === paletteId);

  return (
    <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2">
      <div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Palette groups">
          {palettes.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`palette-tab-${p.id}`}
              aria-pressed={paletteId === p.id}
              onClick={() => {
                setPaletteId(p.id);
                setSelected(p.colors[0]);
                trackEvent("palette_change", { palette: p.id });
              }}
              className={`min-h-[44px] rounded-full border px-5 py-2 text-sm font-semibold transition-[background-color,border-color,color] duration-200 ${
                paletteId === p.id
                  ? "border-ember bg-ember text-white"
                  : "border-slate-300 bg-white text-slate-600 hover:border-ember hover:text-ember-dark"
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
        <p className="mt-4 text-sm text-slate-500">{palette.blurb}</p>
        <div className="mt-6 grid grid-cols-3 gap-4 sm:grid-cols-6">
          {palette.colors.map((c) => (
            <button
              key={c.name}
              type="button"
              data-testid={`swatch-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              aria-pressed={selected.name === c.name}
              aria-label={`Preview ${c.name}`}
              onClick={() => {
                setSelected(c);
                trackEvent("swatch_select", { colour: c.name });
              }}
              className="group flex min-h-[44px] flex-col items-center gap-2"
            >
              <span
                className={`relative h-14 w-14 rounded-2xl border shadow-sm transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1 ${
                  selected.name === c.name ? "ring-2 ring-ember ring-offset-2" : "border-slate-200"
                }`}
                style={{ backgroundColor: c.hex }}
              >
                {selected.name === c.name && (
                  <Check className="absolute inset-0 m-auto h-5 w-5 text-white drop-shadow" aria-hidden="true" />
                )}
              </span>
              <span className="text-center text-[11px] font-medium leading-tight text-slate-600">{c.name}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
          <RoomPreview color={selected.hex} />
          <div className="flex items-center justify-between border-t border-slate-100 px-5 py-4">
            <div>
              <p className="font-display text-base font-semibold text-slate-900" data-testid="selected-colour-name">{selected.name}</p>
              <p className="text-xs uppercase tracking-widest text-slate-400">{selected.hex}</p>
            </div>
            <span className="h-10 w-10 rounded-full border border-slate-200" style={{ backgroundColor: selected.hex }} aria-hidden="true" />
          </div>
        </div>
        <p className="mt-4 text-xs leading-relaxed text-slate-500">
          Screens show colour differently from real paint — lighting, devices and finishes all change what you
          see. Treat this preview as inspiration only, and always confirm your choice with a physical paint
          sample on the actual wall before committing.
        </p>
      </div>
    </div>
  );
}
